import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FC } from 'react';
import { Box } from '@mui/material';
import {
  Report,
  REPORT_PAGE_DEFAULT_PADDING,
  useGetScalingSize,
} from '@ui/Report';
import { useReportContext } from '@ui/Report/report.context';
import { Design } from '@features/design';
import {
  useDesignActivities,
  useProgramActivityMix,
} from '@features/design/hooks/activities.hooks';
import {
  useBuildingDesign,
  useBuildingLocation,
} from '@features/design/hooks/design.hooks';
import {
  computeBuildingFitZoom,
  getBuildingFootprintBbox,
} from '@features/design/helpers/building-fit-zoom.helpers';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import { isEmptyBuildingDesign } from '@shared/helpers/design/design.helpers';
import { getAllStoreys } from '@shared/helpers/design/storey.helpers';
import { activityRefGuards } from '@shared/helpers/design/guards/activity-guards.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { useUserOrganization } from '@client/store/organization/organization.hook';
import {
  useBuildingLifetime,
  useProject,
} from '@client/store/project/project.hook';
import { useEditProjectDialogStore } from '@features/projects/EditProject/edit-project.store';
import type { Map3dReadonlyCamera } from '@features/design/Map3d';
import type { MapCameraState } from '@shared/models/design/map-render.model';
import type { DesignMap } from '@features/design/map';
import { useConfig } from '@client/providers/ConfigProvider';
import {
  getPrintCoverReadyAction,
  shouldAcceptPrintCoverMapIdle,
  shouldShowLiveCoverMap,
} from '@client/helpers/report-cover-image-status.helpers';
import { PhasesCard } from '@features/proposals/ScopeSelector/PhasesCard';
import { requestScopeOverlay } from '@features/scope/scope-overlay.store';
import { useSelectedLifecycles } from '@features/lifecycles/lifecycle-scope.hooks';
import { useSelectedSbefGroups } from '@features/sbef/sbef-groups.store';
import { useReportPdfPrint } from '@client/pages/PrintView/report-pdf-print.context';
import {
  markCoverMapReady,
  REPORT_COVER_MAP_SLOT_ATTR,
} from '@client/pages/PrintView/report-pdf-ready.helpers';
import { useReportCoverImage } from '../report-cover-image.context';
import { buildReportCoverScopeLines } from './report-cover-scope.helpers';
import { ReportCoverScopeLines } from './ReportCoverScopeLines';

type ReportCoverPageProps = {
  title: string;
  gfa: number;
};

/** Pitch (deg) for the report cover map — oblique view, not top-down. */
const COVER_CAMERA_PITCH = 75;
const COVER_CAMERA_BEARING = -17.6;
/**
 * Fallback zoom used when the building footprint can't be measured (empty
 * design or zero-sized container). Roughly matches a typical urban block at
 * the report's default container width.
 */
const COVER_CAMERA_FALLBACK_ZOOM = 16.5;
/**
 * Fraction of the map slot's shorter dimension the footprint should occupy.
 * Main pane: conservative margin for pitched camera.
 */
const COVER_CAMERA_PADDING_FACTOR_MAIN = 0.45;
/**
 * Sidebar thumbnail uses the same cqw layout but a smaller pixel slot; extra
 * margin avoids the building looking cropped at high pitch.
 */
const COVER_CAMERA_PADDING_FACTOR_THUMBNAIL = 0.36;

/**
 * Narrow a cover-map camera to finite zoom/pitch/bearing for generate-image.
 */
const toCoverMapCamera = (
  camera: Map3dReadonlyCamera | undefined,
): MapCameraState | undefined => {
  if (!camera) return undefined;
  if (
    !guards.is.finiteNumber(camera.zoom) ||
    !guards.is.finiteNumber(camera.pitch) ||
    !guards.is.finiteNumber(camera.bearing)
  ) {
    return undefined;
  }
  return {
    zoom: camera.zoom,
    pitch: camera.pitch,
    bearing: camera.bearing,
  };
};

export const ReportCoverPage: FC<ReportCoverPageProps> = ({ title, gfa }) => {
  const getScalingSize = useGetScalingSize();
  const { isPrint } = useReportContext();
  const { isCoverFrozen, skipLiveCoverMap } = useReportPdfPrint();
  const { status, imageUrl, registerMapCapture } = useReportCoverImage();
  const [config] = useConfig();
  const showStoredImage =
    (status === 'ready' || status === 'removing') && Boolean(imageUrl);
  const activities = useDesignActivities();
  const programActivityMix = useProgramActivityMix();
  const organization = useUserOrganization();
  const organizationName = organization?.display_name;
  const project = useProject();
  const description = project.description;
  const openEditDialog = useEditProjectDialogStore((s) => s.set);
  const design = useBuildingDesign();
  const location = useBuildingLocation();
  const selectedLifecycles = useSelectedLifecycles();
  const selectedSbefGroups = useSelectedSbefGroups();
  const lifetime = useBuildingLifetime();

  // Cover frames every building, not only the selected one (DEV-2576).
  const footprintBbox = useMemo(
    () => getBuildingFootprintBbox(getAllStoreys(design)),
    [design],
  );

  const scopeLines = useMemo(
    () =>
      buildReportCoverScopeLines(
        selectedLifecycles,
        lifetime,
        selectedSbefGroups,
      ),
    [selectedLifecycles, lifetime, selectedSbefGroups],
  );

  // Cover page renders twice (sidebar thumbnail + main). Each instance keeps
  // its own fit-zoom — a single shared zoom cannot fit both slot sizes (main-only
  // cropped the thumb; min of both shrank the main building).
  const isThumbnailRef = useRef(false);
  const mapBoxRef = useRef<HTMLDivElement>(null);
  const [coverCamera, setCoverCamera] = useState<
    Map3dReadonlyCamera | undefined
  >(undefined);
  const coverCameraRef = useRef(coverCamera);
  coverCameraRef.current = coverCamera;
  const mapRef = useRef<DesignMap | undefined>(undefined);
  const didRegisterCaptureRef = useRef(false);
  const recomputeRafRef = useRef<number | undefined>(undefined);

  const setPageRef = useCallback((node: HTMLDivElement | null) => {
    isThumbnailRef.current = Boolean(node?.closest('[data-thumbnail]'));
  }, []);

  const recomputeCoverCamera = useCallback(() => {
    const thumb = isThumbnailRef.current;
    const rect = mapBoxRef.current?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    const height = rect?.height ?? 0;
    if (width <= 0 || height <= 0) return;

    const paddingFactor = thumb
      ? COVER_CAMERA_PADDING_FACTOR_THUMBNAIL
      : COVER_CAMERA_PADDING_FACTOR_MAIN;
    const fitZoom = footprintBbox
      ? computeBuildingFitZoom({
          containerWidth: width,
          containerHeight: height,
          latitudeDegrees: location.lat,
          bbox: footprintBbox,
          paddingFactor,
        })
      : undefined;

    const nextZoom = fitZoom ?? COVER_CAMERA_FALLBACK_ZOOM;
    setCoverCamera((prev) => {
      if (
        prev?.zoom === nextZoom &&
        prev.pitch === COVER_CAMERA_PITCH &&
        prev.bearing === COVER_CAMERA_BEARING
      ) {
        return prev;
      }
      return {
        zoom: nextZoom,
        pitch: COVER_CAMERA_PITCH,
        bearing: COVER_CAMERA_BEARING,
      };
    });
  }, [footprintBbox, location.lat]);

  useEffect(() => {
    const el = mapBoxRef.current;
    if (!el) return;
    // Coalesce multiple ResizeObserver callbacks per frame so we recompute
    // the fit-zoom at most once per animation frame instead of once per
    // observed contentRect change.
    const scheduleRecompute = () => {
      if (recomputeRafRef.current !== undefined) return;
      recomputeRafRef.current = requestAnimationFrame(() => {
        recomputeRafRef.current = undefined;
        recomputeCoverCamera();
      });
    };
    scheduleRecompute();
    const ro = new ResizeObserver(scheduleRecompute);
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (recomputeRafRef.current !== undefined) {
        cancelAnimationFrame(recomputeRafRef.current);
        recomputeRafRef.current = undefined;
      }
    };
  }, [recomputeCoverCamera]);

  /**
   * PrintView must not snapshot until Map3d has reached a real idle with the
   * building in the scene. An empty-design idle is only the transparent style.
   */
  const isEmptyDesign = isEmptyBuildingDesign(getAllStoreys(design));
  const handleCoverMapIdle = useCallback(() => {
    if (!isPrint) return;
    if (!shouldAcceptPrintCoverMapIdle(isEmptyDesign)) return;
    markCoverMapReady(document.documentElement);
  }, [isPrint, isEmptyDesign]);

  useEffect(() => {
    if (!isPrint) return;
    const action = getPrintCoverReadyAction({
      status,
      hasStoredImage: showStoredImage,
      hasMapboxToken: Boolean(config.mapboxAccessToken),
      skipLiveCoverMap,
    });
    switch (action) {
      case 'wait':
      case 'wait-for-map-idle':
        return;
      case 'mark-ready':
        markCoverMapReady(document.documentElement);
        return;
      default:
        assertUnreachable(action);
    }
  }, [
    isPrint,
    status,
    showStoredImage,
    config.mapboxAccessToken,
    skipLiveCoverMap,
  ]);

  const publishMapCapture = useCallback(
    (map: DesignMap | undefined) => {
      if (!map) {
        if (didRegisterCaptureRef.current) {
          registerMapCapture(undefined);
          didRegisterCaptureRef.current = false;
        }
        return;
      }
      const isThumb = Boolean(
        map.map?.getContainer()?.closest('[data-thumbnail]'),
      );
      if (isThumb) return;
      // Enable Create only once fit-zoom has produced a finite camera.
      if (!toCoverMapCamera(coverCameraRef.current)) {
        if (didRegisterCaptureRef.current) {
          registerMapCapture(undefined);
          didRegisterCaptureRef.current = false;
        }
        return;
      }
      didRegisterCaptureRef.current = true;
      registerMapCapture({
        captureScreenshot: () => map.captureScreenshot(),
        captureVisibleFrame: () => map.captureVisibleFrame(),
        getCamera: () => toCoverMapCamera(coverCameraRef.current),
      });
    },
    [registerMapCapture],
  );

  const handleMapReady = useCallback(
    (map: DesignMap | undefined) => {
      mapRef.current = map;
      publishMapCapture(map);
    },
    [publishMapCapture],
  );

  useEffect(() => {
    publishMapCapture(mapRef.current);
  }, [coverCamera, publishMapCapture]);

  const openScopeSelector = useCallback(() => {
    requestScopeOverlay();
  }, []);

  const handleDescriptionClick = () => {
    openEditDialog({
      type: 'project',
      mode: 'edit',
      id: project.id,
      focusField: 'description',
    });
  };

  const programLabels = useMemo(() => {
    const programActivities = activities
      .filter((a) => activityRefGuards.is.programActivityType(a.type))
      .sort(
        (a, b) =>
          (programActivityMix[b.type] ?? 0) - (programActivityMix[a.type] ?? 0),
      );
    return programActivities.map((a) => getActivityTypeLabel(a)).join(', ');
  }, [activities, programActivityMix]);

  const showLiveCoverMap = shouldShowLiveCoverMap({
    hasCamera: Boolean(coverCamera),
    hasStoredImage: showStoredImage,
    status,
    isPrint,
    isCoverFrozen,
    skipLiveCoverMap,
  });

  return (
    <Report.Page padding={0} verticalAlign="stretch" hideFooter>
      <Box
        ref={setPageRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Live 3D building, or a persisted Gemini cover image in the same slot.
            Print prefers the stored image; otherwise Map3d is mounted and
            PrintView snapshots the WebGL canvas after Mapbox idle. */}
        <Box
          ref={mapBoxRef}
          // Slot marker so PrintView snapshots only this WebGL canvas, not Chart.js.
          {...{ [REPORT_COVER_MAP_SLOT_ATTR]: true }}
          sx={{
            position: 'absolute',
            left: '41.15%',
            top: '27.69%',
            width: '52.66%',
            height: '65.65%',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {showStoredImage && imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            showLiveCoverMap &&
            coverCamera && (
              <Design.Map3d
                variant="readonly"
                readonlyCamera={coverCamera}
                readonlyLogoCropPx={0}
                readonlyHideMapboxLogo
                onMapReady={isPrint ? undefined : handleMapReady}
                onIdle={isPrint ? handleCoverMapIdle : undefined}
              />
            )
          )}
        </Box>

        {/* Organization name */}
        {organizationName && (
          <Box
            sx={{
              position: 'absolute',
              left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
              top: getScalingSize(REPORT_PAGE_DEFAULT_PADDING),
              opacity: 0.5,
              fontFamily: '"Roboto Slab", serif',
              fontWeight: 400,
              fontSize: getScalingSize(1.25),
              lineHeight: 1.32,
              color: 'black',
              whiteSpace: 'nowrap',
            }}
          >
            {organizationName}
          </Box>
        )}

        {/* Date */}
        <Box
          sx={{
            position: 'absolute',
            right: `${REPORT_PAGE_DEFAULT_PADDING}%`,
            top: getScalingSize(REPORT_PAGE_DEFAULT_PADDING),
            opacity: 0.5,
            fontFamily: '"Roboto Slab", serif',
            fontWeight: 400,
            fontSize: getScalingSize(1.25),
            lineHeight: 1.32,
            color: 'black',
            whiteSpace: 'nowrap',
          }}
        >
          {new Date().toISOString().slice(0, 10)}
        </Box>

        {/* Project title */}
        <Box
          sx={{
            position: 'absolute',
            left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
            top: '14.35%',
            opacity: 0.8,
            fontFamily: '"Roboto Slab", serif',
            fontWeight: 400,
            fontSize: getScalingSize(5.67),
            lineHeight: 1.32,
            letterSpacing: getScalingSize(-0.113),
            color: 'black',
            whiteSpace: 'nowrap',
            maxWidth: `calc(100% - ${REPORT_PAGE_DEFAULT_PADDING}% - 2%)`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Box>

        {/* Programs */}
        {programLabels && (
          <Box
            sx={{
              position: 'absolute',
              left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
              top: '27.22%',
              opacity: 0.8,
              fontFamily: '"Roboto Slab", serif',
              fontWeight: 400,
              fontSize: getScalingSize(1.67),
              lineHeight: 1.32,
              letterSpacing: getScalingSize(-0.033),
              color: 'black',
              whiteSpace: 'nowrap',
            }}
          >
            {programLabels}
          </Box>
        )}

        {/* GFA */}
        {gfa > 0 && (
          <Box
            sx={{
              position: 'absolute',
              left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
              top: `calc(36.39% - ${getScalingSize(1.79)})`,
              opacity: 0.8,
              fontFamily: '"Roboto Slab", serif',
              fontWeight: 400,
              fontSize: getScalingSize(2.71),
              lineHeight: 1.32,
              letterSpacing: getScalingSize(-0.054),
              color: 'black',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'baseline',
              gap: getScalingSize(0.2),
            }}
          >
            <span>{getDisplayNumber(gfa)}</span>
            <Box component="span" sx={{ opacity: 0.5 / 0.8 }}>
              m²
            </Box>
          </Box>
        )}

        {/* Calculation scope on the cover (DEV-3533): LCA modules and SBEF
            building parts were missing from the PDF entirely. First page is
            where the export is expected to declare omfattning. */}
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
            top: '42%',
            flexDirection: 'column',
            gap: getScalingSize(0.25),
            maxWidth: `calc(41.15% - ${REPORT_PAGE_DEFAULT_PADDING}% - 1%)`,
            overflow: 'visible',
            fontFamily: '"Roboto Slab", serif',
            fontWeight: 400,
            fontSize: getScalingSize(0.83),
            lineHeight: 1.32,
            color: 'black',
          }}
        >
          <ReportCoverScopeLines
            interactive={!isPrint}
            lines={[
              { label: 'LCA modules', value: scopeLines.lcaModules },
              { label: 'Building parts', value: scopeLines.buildingParts },
            ]}
            lineGap={getScalingSize(0.6)}
            rowGap={getScalingSize(0.25)}
            labelMinWidth={getScalingSize(7)}
            onOpen={openScopeSelector}
          >
            <PhasesCard hideTrigger panelAlignment="left" />
          </ReportCoverScopeLines>
        </Box>

        {/* Description */}
        {description && (
          <Box
            onClick={handleDescriptionClick}
            sx={{
              position: 'absolute',
              left: `${REPORT_PAGE_DEFAULT_PADDING}%`,
              top: `calc(46% + ${getScalingSize(3.74)})`,
              opacity: 0.7,
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 400,
              fontSize: getScalingSize(0.83),
              lineHeight: 1.5,
              color: 'black',
              maxWidth: `calc(41.15% - ${REPORT_PAGE_DEFAULT_PADDING}% - 1%)`,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 14,
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              cursor: 'pointer',
            }}
          >
            {description}
          </Box>
        )}
      </Box>
    </Report.Page>
  );
};
